import { Box, InputAdornment, TextField } from "@mui/material";
import TextFieldComponent from "../../controls/TextField";
import React, { ChangeEvent, useState, FocusEvent, useRef, useEffect } from "react";
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

type SearchDropdownProps = {
    onChange: (_: any, data: any) => void,
    items: (string | null | undefined)[],
    placeholder: string,
    value: string,
    error: boolean,
    helperText: string
};

const SearchDropdown: React.FC<SearchDropdownProps> = ({ placeholder, items, onChange, value, error, helperText, ...rest }) => {
    const [textValue, setTextValue] = useState<string>(value);
    const [searchText, setSearchText] = useState<string>("");
    const [hoverItem, setHoverItem] = useState<string>("");
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [openUpOffset, setOpenUpOffset] = useState<number>(0);
    const [maxHeight, setMaxHeight] = useState<string>("20rem");
    const [textfieldFocued, setTextfieldFocued] = useState<boolean>(false);
    const mainTextfield = useRef<HTMLInputElement>()
    const dropdownbox = useRef<HTMLInputElement>()
    const viewportCenter = window.innerHeight / 2;
    
    useEffect(() => {
        if(openUp()){
            const dropdownHeight = dropdownbox.current?.getBoundingClientRect().height ?? 0
            const textBoxHeight = mainTextfield.current?.getBoundingClientRect().height ?? 0
            setOpenUpOffset(-(dropdownHeight + textBoxHeight))
        }else{
            setOpenUpOffset(0);
        }
    }, [dropdownbox.current, mainTextfield.current, isOpen])



    const toggleDropdown = () => { 
        openUp();
        if(textfieldFocued){
            setIsOpen(true); 
        }else{
            setIsOpen(!isOpen); 
        }
        if(!isOpen){
            setSearchText("");
        }
    };
    
    const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target?.value ?? '');
    };

    const openUp = () => {
        if(!mainTextfield.current){
            return false;
        }
        const boundingBox = mainTextfield.current.getBoundingClientRect();
        const centerPoint = boundingBox.top + (boundingBox.height / 2);        
        setMaxHeight(centerPoint < viewportCenter ? "20rem" : `${(mainTextfield.current?.getBoundingClientRect().top ?? 0) - 250}px`)
        return centerPoint > viewportCenter;
    }
    
    const filteredItems = items.filter((item) =>
        item?.toLowerCase().includes(searchText.toLowerCase())
    );

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        setTextValue(e.target.value);
        setSearchText(e.target.value);
        if(checkValid(e.target.value)) {
            onChange(e, e.target.value);
        }
    }

    const handleFocus = (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => {
            setTextfieldFocued(true);
    }

    const handleBlur = (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => {
        if(!checkValid(e.target.value)) {
            setTextValue(value);
            setTextfieldFocued(false);
        }
    }

    const checkValid = (newValue: string) => {
        return items.some(item => item === newValue)
    }

    return (
        <>            
            {isOpen && <Box onClick={() => setIsOpen(false)} style={{ position: "fixed", left: 0, top:0, width:'100%', height:'100%', zIndex: 999 }} />}
                        
            <Box>
                <TextFieldComponent                    
                    {...rest}
                    inputRef={mainTextfield}
                    onChange={ handleChange }
                    onClick={toggleDropdown}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    label={placeholder}
                    value={textValue}
                    error={error} variant={'outlined'}
                    helperText={error ? 'Required' : helperText} 
                    fullWidth
                    style={{ zIndex: 1001 }}
                    InputProps={{
                        endAdornment: (mainTextfield.current?.value.length ?? 0) > 0 ?
                            (<InputAdornment position="end">
                                <ClearIcon style={{cursor: "pointer"}} onClick={() => {
                                    setTextValue("");
                                    mainTextfield.current?.focus();
                                }} />
                            </InputAdornment>)
                            : null
                    }}
                />

                {isOpen && (
                    <Box style={{ position: "relative" }} > 
                        <Box
                            ref={dropdownbox}
                            boxShadow={3} 
                            style={{
                                borderRadius:
                                    openUpOffset === 0 
                                        ?'0px 0px 5px 5px'
                                        :'35px 35px 0px 0px',
                                position: "absolute",
                                background: "white",
                                width: "100%", 
                                zIndex:10000,
                                top: openUpOffset + 'px'
                            }}
                        >                    
                            <TextField
                                style={{ width: 'calc(100% - 20px)' }}
                                onChange={handleSearch}
                                placeholder="Search..."
                                value={searchText}
                                InputProps={{
                                    style: { margin: '10px', borderRadius: '2rem', width: '100%' },
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {searchText.length > 0 ? <ClearIcon style={{cursor: "pointer"}} onClick={() => setSearchText("")} /> : <SearchIcon />}
                                        </InputAdornment>
                                    )
                                }}
                            />                    
                            <Box
                                style={{
                                    borderRadius: 
                                        openUpOffset === 0 
                                            ?'0px 0px 5px 5px'
                                            :'35px 35px 0px 0px',
                                    position: "relative",
                                    overflowY: "scroll",
                                    overflowX: "hidden", 
                                    maxHeight: maxHeight,
                                }}
                            >                        
                                {filteredItems.map((item, index) => {
                                    return (
                                        <Box
                                            onMouseOver={() => {setHoverItem(item ?? "")}}
                                            onMouseLeave={() => {setHoverItem("")}}
                                            key={index}
                                            onClick={(e) => {
                                                setTextValue(item ?? "");
                                                onChange(e, item ?? "");
                                                setIsOpen(false);
                                            }}
                                            style={{
                                                background: (hoverItem === item || textValue === item ? "#efefef" : "white"),
                                                padding: "5px 10px",
                                                cursor: "pointer"
                                            }}
                                        >
                                            {item}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>
        </>
  );
};

export default SearchDropdown;
